/**
 * ShareIconButton renders the trigger icon for the share-room dialog.
 *
 * Why: Extracting the trigger keeps ShareDialog focused on dialog content/nav,
 * matching how GameControlButton is kept separate from InfoDialog.
 *
 * When to use: Passed as the `trigger` to ShareDialog, rendered by Header.
 * Must forward arbitrary props (esp. onClick) and merge refs - Radix's
 * `Dialog.Trigger asChild` clones this element and injects both, and without
 * forwarding them the trigger silently does nothing when clicked.
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { ShareIcon } from '../../library/Icons/ShareIcon'
import { headerTestIds } from './header.testIds'

export interface ShareIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Ref for focus management (from header navigation) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

/** Merge multiple refs into one callback ref */
function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value
      }
    })
  }
}

export const ShareIconButton = React.forwardRef<HTMLButtonElement, ShareIconButtonProps>(
  ({ buttonRef, className, tabIndex, isFocused, accentColor, accentInkColor = '#ffffff', backgroundColor, ...props }, ref) => {
    return (
      <IconButton
        {...props}
        ref={mergeRefs(ref, buttonRef)}
        data-testid={headerTestIds.copyRoomButton}
        className={cn(
          "flex h-[var(--menu-btn-size)] w-[var(--menu-btn-size)] items-center justify-center rounded-lg transition-colors",
          isFocused && "outline-none",
          className
        )}
        style={{
          ...(accentColor ? { color: accentColor } : {}),
          ...(isFocused && accentColor ? {
            boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
          } : {}),
        }}
        tabIndex={tabIndex}
        title="Share room"
        aria-label="Share room"
        onMouseEnter={(e) => {
          if (accentColor) {
            e.currentTarget.style.backgroundColor = accentColor
            e.currentTarget.style.color = accentInkColor
          }
        }}
        onMouseLeave={(e) => {
          if (accentColor) {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = accentColor
          }
        }}
        icon={<ShareIcon width="var(--menu-icon-size)" height="var(--menu-icon-size)" />}
      />
    )
  }
)
ShareIconButton.displayName = 'ShareIconButton'
